<template>
  <div class="job-card">
    <div class="job-card-header">
      <div class="job-icon pointer" :title="job.company_name">
        <img 
          :src="logoURL + job.company_logo" 
          :alt="job.company_name" 
          class="card-logo" 
          loading="lazy" 
          @error="handleImageError"
        />
      </div>
      <div class="job-card-title">
        <router-link
          :to="{
            name: 'jobDetail',
            params: {
              name: toSeoUrl(`${job.company_name}-${job.short_name}`),
              id: job.id,
            },
          }"
          class="job-name"
          :title="job.short_name"
        >
          {{ job.short_name }}
          <i class="pi pi-external-link job-link-icon"></i>
        </router-link>
        <div class="job-posted">
          Posted at {{ formatDate(job.createDate ? job.createDate : job.create_date) }} by <strong>{{ job.company_name }}</strong>
        </div>
      </div>
      
      <!-- Options button -->
      <div v-if="job.show_threedots == 1">
        <CButton
          icon="pi pi-ellipsis-v"
          variant="ghost"
          color="default"
          iconOnly
          v-tooltip="'More Actions'"
          @click="onToggleMenu($event)"
        />
      </div>
    </div>
    
    <div class="job-card-tags">
      <span class="tag tag-job-type">{{ job.job_type_name }}</span>
      <span class="tag tag-recruitment">{{ job.recruitment_type_name }}</span>
      <span v-if="job.remote_working_type_name" class="tag tag-remote">{{ job.remote_working_type_name }}</span>
    </div>
    
    <div v-if="job.job_description" class="job-card-description" v-html="truncateDescription(job.job_description, 150)"></div>
    
    <div class="job-additional-info">
      <div class="tag tag-location">
        <i class="pi pi-map-marker"></i>
        {{
          (job.location_city_name ? job.location_city_name : '') +
          (job.location_city_name && job.location_country_name ? '/' : '') +
          (job.location_country_name ? job.location_country_name : '')
        }}
      </div>
      
      <div class="tag tag-reward" v-if="job.reward">
        <i class="pi pi-wallet"></i>
        {{ formattedAmount(job) }}
      </div>
      
      <div class="tag tag-active" v-if="showActiveCandidateCount">
        <i class="pi pi-users"></i>
        {{ job.active_candidate_cnt || 0 }} Active
      </div>
      
      <div class="tag tag-waiting" v-if="showWaitingApprovalCandidateCount">
        <i class="pi pi-user-plus"></i>
        {{ job.waiting_approval_candidate_cnt || 0 }} Waiting
      </div>
    </div>
    
    <!-- Progress and action buttons -->
    <div class="card-footer-actions">
      <div class="job-progress" v-if="showProgressPct">
        <div class="progress-container">
          <div class="progress-label">
            <span>Progress</span>
            <span class="progress-value">{{ job.progress_pct }}%</span>
          </div>
          <ProgressBar
            class="ch-progress"
            :value="job.progress_pct"
            :showValue="false"
          />
        </div>
      </div>
      
      <!-- Conditional action buttons -->
      <div class="main-action-buttons">
        <!-- For Application.vue - List Candidates button -->
        <template v-if="showListCandidates">
          <CButton
            class="w-100"
            @click="onListCandidatesClick"
            variant="solid" 
            color="secondary"
            icon="pi pi-users"
            label="List Candidates"
          />
        </template>
        
        <!-- For JobOpenings.vue - Refer and Apply buttons -->
        <div v-else class="d-flex gap-2 w-100" :class="getButtonsLayoutClass">
          <CButton
            v-if="shouldShowReferButton"
            class="flex-1"
            @click="onReferClick"
            variant="solid"
            color="secondary"
            icon="pi pi-user-edit"
            label="Refer"
            :disabled="job.show_refer_button == 0"
          />
          <CButton
            v-if="shouldShowApplyButton"
            class="flex-1"
            @click="onApplyClick"
            variant="solid"
            color="primary"
            icon="pi pi-check-circle"
            :label="job.show_apply_button == 0 ? 'Applied' : 'Apply'"
            :disabled="job.show_apply_button == 0"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { logoURL } from '@/api/Services'
import moment from 'moment'

export default {
  name: 'JobCard',
  props: {
    job: {
      type: Object,
      required: true,
    },
    showActiveCandidateCount: {
      type: Boolean,
      default: true
    },
    showWaitingApprovalCandidateCount: {
      type: Boolean,
      default: true
    },
    showProgressPct: {
      type: Boolean,
      default: true
    },
    formattedAmount: {
      type: Function,
      required: true
    },
    showListCandidates: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      logoURL,
      imageError: false
    }
  },
  methods: {
    formatDate(date) {
      return moment(String(date)).format('DD.MM.YYYY')
    },
    toSeoUrl(text) {
      // Convert to lowercase
      let seoText = text.toLowerCase()

      // Convert Turkish characters to English equivalents
      const map = {
        ç: 'c',
        ğ: 'g',
        ı: 'i',
        ö: 'o',
        ş: 's',
        ü: 'u',
        Ç: 'C',
        Ğ: 'G',
        İ: 'I',
        Ö: 'O',
        Ş: 'S',
        Ü: 'U',
      }

      seoText = seoText.replace(/[çğışüö]/g, (match) => map[match] || match)

      // Replace special characters and spaces with '-'
      seoText = seoText.replace(/[^a-z0-9]+/g, '-')

      // Remove '-' at the beginning and end
      seoText = seoText.replace(/^-+|-+$/g, '')

      return seoText
    },
    truncateDescription(html, maxLength) {
      if (!html) return '';
      // Remove HTML tags
      const text = html.replace(/<[^>]*>/g, '');
      if (text.length <= maxLength) return html;
      return text.substring(0, maxLength) + '...';
    },
    onReferClick() {
      this.$emit('refer', this.job)
    },
    onApplyClick() {
      this.$emit('apply', this.job)
    },
    onListCandidatesClick() {
      this.$emit('list-candidates', this.job)
    },
    onToggleMenu(event) {
      this.$emit('toggle-menu', {event, job: this.job})
    },
    handleImageError(e) {
      // Replace with fallback or placeholder image on error
      e.target.src = '/img/placeholder.png'; // Using an existing placeholder image
      this.imageError = true;
    }
  },
  computed: {
    shouldShowReferButton() {
      return this.job.show_refer_button !== -1;
    },
    shouldShowApplyButton() {
      return this.job.show_apply_button !== -1;
    },
    getButtonsLayoutClass() {
      // If either button is hidden (-1), use a different class for layout
      if (this.job.show_apply_button === -1 || this.job.show_refer_button === -1) {
        return 'justify-content-center';
      }
      return '';
    }
  }
}
</script>

<style lang="scss" scoped>
.job-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  color: #444;
  border: 1px solid rgba(0, 0, 0, 0.05);
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0;
}

.job-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.job-card-header {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  flex-wrap: wrap;
}

.job-icon {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 10px;
}

.card-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.job-card-title {
  flex: 1;
  min-width: 0; /* Important for text-overflow to work */
  overflow: hidden;
}

.job-name {
  font-weight: 600;
  font-size: 16px;
  color: #015cac;
  text-decoration: none;
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.job-link-icon {
  font-size: 12px;
  margin-left: 4px;
  position: relative;
  top: -2px;
}

.job-posted {
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 12px;
}

.job-posted strong {
  font-weight: 600;
  color: #495057;
}

.job-card-tags,
.job-additional-info {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.tag {
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  margin-right: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.tag-job-type {
  background-color: rgba(252, 108, 42, 0.05);
  color: #fc6c2a;
  border: 1px solid rgba(252, 108, 42, 0.2);
}

.tag-recruitment {
  background-color: rgba(1, 92, 172, 0.05);
  color: #015cac;
  border: 1px solid rgba(1, 92, 172, 0.2);
}

.tag-remote {
  background-color: rgba(108, 117, 125, 0.05);
  color: #6c757d;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.job-card-description {
  font-size: 13px;
  color: #444;
  margin-bottom: 12px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
  width: 100%;
  max-width: 100%;
  word-break: break-word;
}

.job-additional-info {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
}

.tag-location {
  background-color: rgba(108, 117, 125, 0.05);
  color: #495057;
  border: 1px solid rgba(108, 117, 125, 0.2);
}

.tag-reward {
  background-color: rgba(252, 108, 42, 0.05);
  color: #fc6c2a;
  border: 1px solid rgba(252, 108, 42, 0.2);
}

.tag-active {
  background-color: rgba(0, 148, 50, 0.05);
  color: #009432;
  border: 1px solid rgba(0, 148, 50, 0.2);
}

.tag-waiting {
  background-color: rgba(255, 153, 0, 0.05);
  color: #FF9900;
  border: 1px solid rgba(255, 153, 0, 0.2);
}

.card-footer-actions {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 100%;
}

.progress-container {
  background-color: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.progress-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: #495057;
  font-weight: 500;
  font-size: 13px;
}

.progress-value {
  color: #009432;
  font-weight: 600;
}

.ch-progress {
  height: 8px;
  background: #f2f2f2;
  border-radius: 4px;
  overflow: hidden;
}

.main-action-buttons {
  display: flex;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

/* Mobile optimizations */
@media (max-width: 576px) {
  .job-card {
    padding: 12px;
    border-radius: 8px;
    margin: 0;
    width: 100%;
    max-width: 100%;
  }
  
  .main-action-buttons {
    gap: 5px;
    flex-wrap: wrap;
  }
  
  .job-icon {
    width: 50px;
    height: 50px;
  }
  
  .job-name {
    font-size: 14px;
  }
  
  .job-posted {
    font-size: 11px;
  }
  
  .tag {
    font-size: 11px;
    padding: 2px 6px;
  }
}

.flex-1 {
  flex: 1;
  min-width: 0; /* Allow button to shrink below its content size */
}

/* Force all buttons to respect container width */
button, 
.p-button {
  max-width: 100%;
  overflow: hidden;
}
</style> 